import React, {useEffect, useState} from 'react';
import {Button, DatePicker, Input, message, Radio, Select} from 'antd';
import {FolderOpenOutlined, SaveOutlined} from '@ant-design/icons';
import axios from 'axios';
import {TaskData} from '../../../../types/TaskData';
import './SystemPlanningOrderCreation.css';
import dayjs, {Dayjs} from "dayjs";
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import {StatusUtils} from "../../../../utils/StatusUtils";
import {getConfig} from "../../../../config";
import {ViewType} from "../../../../types/ViewType";
import {useProjectContext} from "../../../../context/ProjectContext";
import {useDirtyContext} from "../../../../context/DirtyContext";
import HeliosFolderExplorer from "../../../heliosFolderExplorer/HeliosFolderExplorer";
import locale from "antd/es/date-picker/locale/de_DE";

const { Option } = Select;
const { TextArea } = Input;
const { API_BASE_URL } = getConfig();

interface SystemPlanningOrderCreationProps {
    data: TaskData;
    viewType: ViewType;
    onSave?: (data: any) => void;
    projectNo?: string;
    onRefreshTable: () => void;
}

const SystemPlanningOrderCreation: React.FC<SystemPlanningOrderCreationProps> = ({ data, viewType, onRefreshTable, onSave, projectNo }) => {
    const [orderType, setOrderType] = useState<string>('');
    const [facadePlanLink, setFacadePlanLink] = useState<string>('');
    const [systemTemplateLink, setSystemTemplateLink] = useState<string>('');
    const [staticsLink, setStaticsLink] = useState<string>('');
    const [architectPlansLink, setArchitectPlansLink] = useState<string>('');
    const [situationPlansLink, setSituationPlansLink] = useState<string>('');
    const [korrexPlanLink, setKorrexPlanLink] = useState<string>(''); // New state for Korrex-Plan
    const [isGalvanized, setIsGalvanized] = useState<boolean | null>(null);
    const [isCoated, setIsCoated] = useState<boolean | null>(null);
    const [colorCode, setColorCode] = useState<string>('');
    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const [effort, setEffort] = useState<number | null>(null);
    const [desiredDeliveryDate, setDesiredDeliveryDate] = useState<Dayjs | null>(null);
    const [comment, setComment] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState(0);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [explorerVisible, setExplorerVisible] = useState(false);
    const [jobCodes, setJobCodes] = useState<NetworkPlanJobCode[]>([]);
    const [mappedJobCode, setMappedJobCode] = useState<NetworkPlanJobCode | null>(null);
    const isDisabled = viewType === ViewType.KORREX;
    const { selectedProject: contextProject } = useProjectContext();
    const { isDirty, setDirty } = useDirtyContext();
    const projectToUse =
        projectNo && projectNo !== "Alle"
            ? projectNo
            : (data && data.projectNo)
                ? data.projectNo
                : contextProject;

    useEffect(() => {
        const fetchJobCodes = async () => {
            try {
                setLoadingCount(prev => prev + 1);
                const response = await axios.get(
                    `${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectToUse}`
                );
                setJobCodes(response.data);
            } catch (error) {
                message.error('Fehler beim Abrufen der Job-Codes');
            } finally {
                setLoadingCount(prev => Math.max(0, prev - 1));
            }
        };
        fetchJobCodes();
    }, []);

    // Map the selected job code to the TaskData's jobCode
    useEffect(() => {
        const matchedJobCode = jobCodes.find((code) => code.alias === data.jobCode);
        setMappedJobCode(matchedJobCode || null);
        if (matchedJobCode) {
            setFacadePlanLink(matchedJobCode.heliosFacadeplan!)
            setSystemTemplateLink(matchedJobCode.heliosSystemplanTemplate!)
            setStaticsLink(matchedJobCode.heliosStatics!)
            setArchitectPlansLink(matchedJobCode.heliosArchitectPlans!)
            setSituationPlansLink(matchedJobCode.heliosSituationPlan!)
            if (matchedJobCode.colorCode) {
                const colors = matchedJobCode.colorCode.split('|').filter(color => color !== "Noch Offen");
                setAvailableColors(colors);
            } else {
                setAvailableColors([]);
            }
            if (viewType !== ViewType.CHANGE)    {
                setIsGalvanized(matchedJobCode.galvanized === '1')
                setIsCoated(matchedJobCode.hasSurface === '1')
                setColorCode("Noch Offen");
                setEffort(matchedJobCode.productOwnerHours);
                if (viewType === ViewType.KORREX) {
                    setOrderType("Korrex");
                } else {
                    setOrderType("Neu");
                }
                const preselectedDate = StatusUtils.calculateWorkingDays(5);
                setDesiredDeliveryDate(preselectedDate);
                if (data.faz) {
                    let parsedDate;
                    if (typeof data.faz === 'string') {
                        parsedDate = dayjs(data.faz, 'DD.MM.YYYY');
                    } else if (typeof data.faz === 'number') {
                        parsedDate = dayjs(data.faz);
                    }
                    if (parsedDate && parsedDate.isValid()) {
                        setDesiredDeliveryDate(parsedDate);
                    } else {
                        console.error('Invalid date format:', data.faz);
                    }
                }
            } else  {
                setOrderType("Neu");
            }
        }
    }, [data, jobCodes]);

    useEffect(() => {
        if (viewType === ViewType.CHANGE && data) {
            const taskId = data?.id;
            if (!taskId) return;
            const fetchOrderDetails = async () => {
                try {
                    setLoadingCount(prev => prev + 1);
                    const response = await axios.get(
                        `${API_BASE_URL}/getSingleNetworkPlanOrder?taskId=${taskId}`
                    );
                    if (response.data) {
                        const { desiredDeliveryDate, comment, galvanized, coated, colorCode, effort } = response.data;
                        setIsGalvanized(galvanized);
                        setIsCoated(coated);
                        setColorCode(colorCode);
                        setEffort(effort ? parseInt(effort, 10) : null);
                        setComment(comment || "");
                        if (desiredDeliveryDate) {
                            const parsedDate = dayjs(desiredDeliveryDate, 'DD.MM.YYYY');
                            if (parsedDate.isValid()) {
                                setDesiredDeliveryDate(parsedDate);
                            } else {
                                console.error("Invalid desiredDeliveryDate format:", desiredDeliveryDate);
                            }
                        }
                    }
                } catch (error) {
                    message.error("Fehler beim Abrufen der Bestellung.");
                } finally {
                    setLoadingCount(prev => Math.max(0, prev - 1));
                }
            };
            fetchOrderDetails();
        }
    }, [viewType, data]);

    useEffect(() => {
        const isKorrex = viewType === ViewType.KORREX;
        const requiredGeneralFieldsAreSet =
            orderType !== null && orderType !== '' &&
            isGalvanized !== null &&
            isCoated !== null &&
            (isCoated ? (colorCode?.trim() || '') !== '' : true) &&
            effort !== null &&
            desiredDeliveryDate !== null
        const requiredDocumentsAreSetForRegularOrder = !isKorrex && (
            (facadePlanLink?.trim() || '') !== '' &&
            (systemTemplateLink?.trim() || '') !== '' &&
            (staticsLink?.trim() || '') !== '' &&
            (architectPlansLink?.trim() || '') !== '' &&
            (situationPlansLink?.trim() || '') !== ''
        );
        const requiredDocumentsAreSetForKorrex = isKorrex && (
            (korrexPlanLink?.trim() || '') !== ''
        );
        const isComplete = requiredGeneralFieldsAreSet &&
            (isKorrex ? requiredDocumentsAreSetForKorrex : requiredDocumentsAreSetForRegularOrder);

        setIsFormComplete(isComplete);
    }, [
        orderType,
        facadePlanLink,
        systemTemplateLink,
        staticsLink,
        architectPlansLink,
        situationPlansLink,
        korrexPlanLink,
        isGalvanized,
        isCoated,
        colorCode,
        effort,
        desiredDeliveryDate,
        comment,
        viewType
    ]);

    const validateHeliosUrl = (value: string) => {
        if (!value.startsWith("h") && value.length === 1) {
            // Show error if the first character is invalid
            message.error("Bitte gebe eine gültige Helios-URL an");
            return false;
        }
        return true;
    };

    const handleInputChange = (value: string, setValue: (value: (((prevState: string) => string) | string)) => void) => {
        setDirty(true);
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            // Allow valid input after the prefix is complete
            setValue(value);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setDirty(false);
        try {
            const payload = {
                id: data.id,
                orderType,
                facadePlanLink,
                systemTemplateLink,
                staticsLink,
                architectPlansLink,
                situationPlansLink,
                korrexPlanLink: orderType === "Korrex" ? korrexPlanLink : null,
                isGalvanized,
                isCoated,
                colorCode: isCoated ? colorCode : null,
                effort,
                desiredDeliveryDate: desiredDeliveryDate
                    ? desiredDeliveryDate.format('DD.MM.YYYY') // Day.js format method
                    : null,
                comment,
            };
            if (viewType === ViewType.NEW_EXTERNAL && onSave) {
                onSave(payload);
            } else  {
                if (viewType === ViewType.CHANGE)   {
                    await axios.post(`${API_BASE_URL}/changeNetworkPlanSystemPlanningOrder`, payload);
                } else  {
                    await axios.post(`${API_BASE_URL}/createNewNetworkPlanSystemPlanningOrder`, payload);
                }
                message.success('Systemplanungs-Bestellung wurde erfolgreich erstellt.');
                onRefreshTable();
            }
        } catch (error) {
            message.error('Systemplanungs-Bestellung konnte nicht erstellt werden');
        } finally {
            setSaving(false);
            onRefreshTable();
        }
    };

    return (
        <div className="container">
            <LoadingModal
                open={saving}
                text="Systemplanungsbeauftagung wird erstellt..."
            />
            <LoadingModal
                open={loadingCount > 0}
                text="Daten werden geladen..."
            />
            <div className="input-group">
                <label><strong>Art der Beauftragung<span style={{color: 'red'}}> *</span></strong></label>
                <Select
                    style={{width: '100%'}}
                    placeholder="Wähle eine Option"
                    disabled={true}
                    value={orderType}
                    onChange={(value) => setOrderType(value)}
                >
                    <Option value="Neu">Neu</Option>
                    <Option value="Korrex">Korrex</Option>
                </Select>
            </div>
            {orderType === "Korrex" && (
                <div className="input-group">
                    <label><strong>Korrex-Plan<span style={{color: 'red'}}> *</span></strong></label>
                    <Input
                        placeholder="helios://"
                        value={korrexPlanLink}
                        onChange={(e) => handleInputChange(e.target.value, setKorrexPlanLink)}
                    />
                </div>
            )}
            <div className="input-group">
                <label><strong>Fassaden- und Grundrisspläne mit Geländer-Typen gekennzeichnet<span
                    style={{color: 'red'}}> *</span></strong></label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        placeholder="helios://"
                        value={facadePlanLink}
                        onChange={(e) => handleInputChange(e.target.value, setFacadePlanLink)}
                        disabled={isDisabled}
                    />
                    <Button
                        shape="circle"
                        onClick={() => setExplorerVisible(true)}
                        icon={<FolderOpenOutlined />}
                    />
                </div>
            </div>

            <div className="input-group">
                <label><strong>Systemplanvorlage (aktualisiert nach Startsitzung)<span style={{color: 'red'}}> *</span></strong></label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        placeholder="helios://"
                        value={systemTemplateLink}
                        onChange={(e) => handleInputChange(e.target.value, setSystemTemplateLink)}
                        disabled={isDisabled}
                    />
                    <Button
                        shape="circle"
                        onClick={() => setExplorerVisible(true)}
                        icon={<FolderOpenOutlined/>}
                    />
                </div>
            </div>
            <div className="input-group">
                <label><strong>Statik, Referenzstatik oder Referenzprojekt<span
                    style={{color: 'red'}}> *</span></strong></label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        placeholder="helios://"
                        value={staticsLink}
                        onChange={(e) => handleInputChange(e.target.value, setStaticsLink)}
                        disabled={isDisabled}
                    />
                    <Button
                        shape="circle"
                        onClick={() => setExplorerVisible(true)}
                        icon={<FolderOpenOutlined/>}
                    />
                </div>
                </div>

            <div className="input-group">
                <label><strong>Relevante Architektenpläne (Grundrisse, Ansichten, Schnitte, Detailpläne)<span
                    style={{color: 'red'}}> *</span></strong></label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        placeholder="helios://"
                        value={architectPlansLink}
                        onChange={(e) => handleInputChange(e.target.value, setArchitectPlansLink)}
                        disabled={isDisabled}
                    />
                    <Button
                        shape="circle"
                        onClick={() => setExplorerVisible(true)}
                        icon={<FolderOpenOutlined/>}
                    />
                </div>
            </div>

            <div className="input-group">
                <label><strong>Relevante Situation<span style={{color: 'red'}}> *</span></strong></label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <Input
                        placeholder="helios://"
                        value={situationPlansLink}
                        onChange={(e) => handleInputChange(e.target.value, setSituationPlansLink)}
                        disabled={isDisabled}
                    />
                    <Button
                        shape="circle"
                        onClick={() => setExplorerVisible(true)}
                        icon={<FolderOpenOutlined/>}
                    />
                </div>
            </div>

                <div className="input-group">
                    <label><strong>Feuerverzinkt<span style={{color: 'red'}}> *</span></strong></label>
                    <Radio.Group
                        value={isGalvanized}
                        onChange={(e) => setIsGalvanized(e.target.value)}
                    >
                        <Radio value={true}>Ja</Radio>
                        <Radio value={false}>Nein</Radio>
                    </Radio.Group>
                </div>

                <div className="input-group">
                    <label><strong>Beschichtung<span style={{color: 'red'}}> *</span></strong></label>
                    <Radio.Group
                        value={isCoated}
                        onChange={(e) => setIsCoated(e.target.value)}
                    >
                        <Radio value={true}>Ja</Radio>
                        <Radio value={false}>Nein</Radio>
                    </Radio.Group>
                    {isCoated && (
                        <Select
                            style={{width: '100%', marginTop: '10px'}}
                            placeholder="Farbcode auswählen"
                            value={colorCode}
                            onChange={(val) => setColorCode(val)}
                        >
                            <Select.Option key="noch_offen" value="Noch Offen">
                                Noch Offen
                            </Select.Option>
                            {availableColors.map((color) => (
                                <Select.Option key={color} value={color}>
                                    {color}
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </div>

                <div className="input-group">
                    <label><strong>Aufwand in Stunden<span style={{color: 'red'}}> *</span></strong></label>
                    <Input
                        type="number"
                        placeholder="Aufwand eingeben"
                        value={effort !== null ? effort : ''}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            const today = dayjs();
                            const maxWorkingHours = desiredDeliveryDate ? StatusUtils.calculateWorkingHours(today, desiredDeliveryDate) : 0;

                            if (value < 1) {
                                message.error('Minimum: 1h');
                            } else if (desiredDeliveryDate && value > maxWorkingHours) {
                                message.error(`Maximum: ${maxWorkingHours} Stunden bis zum gewünschten Lieferdatum`);
                            } else {
                                setEffort(value);
                            }
                        }}
                    />
                </div>

                <div className="input-group">
                    <label><strong>Gewünschtes Lieferdatum (DLZ: 5AT)<span
                        style={{color: 'red'}}> *</span></strong></label>
                    <DatePicker
                        locale={locale}
                        style={{width: '100%'}}
                        value={desiredDeliveryDate}
                        onChange={(date) => setDesiredDeliveryDate(date)}
                        placeholder="Auswahl Datum"
                        showToday
                        format="DD.MM.YYYY"
                        disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                    />
                </div>

                <div className="input-group">
                    <label><strong>Kommentarfeld</strong></label>
                    <TextArea
                        rows={4}
                        placeholder="Kommentar"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </div>
                <div style={{textAlign: 'right'}}>
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        onClick={handleSave}
                        loading={saving}
                        disabled={!isFormComplete}
                        className="action-button"
                    >
                        {viewType === ViewType.KORREX
                            ? "Korrex erstellen"
                            : viewType === ViewType.CHANGE
                                ? "Beauftragung anpassen"
                                : "Beauftragen"}
                    </Button>
                </div>
                <HeliosFolderExplorer
                    open={explorerVisible}
                    onClose={() => setExplorerVisible(false)}
                    onSelectPath={(path: string) => {
                        setFacadePlanLink(path);
                        setDirty(true);
                    }}
                />
            </div>
            );
            };

            export default SystemPlanningOrderCreation;
